import { supabase } from '@/integrations/supabase/client';
import { PUBLIC_PROFILE_COLUMNS, type PublicUserProfile } from '@/lib/profileColumns';
import type { Database } from '@/integrations/supabase/types';

type UserProfile = PublicUserProfile;
type UserFollow = Database['public']['Tables']['user_follows']['Row'];
type UserActivity = Database['public']['Tables']['user_activities']['Row'];

export const socialService = {
  async getFollowing(userId?: string): Promise<(UserFollow & { following: UserProfile })[]> {
    const targetUserId = userId || (await supabase.auth.getUser()).data.user?.id;
    if (!targetUserId) return [];

    const { data, error } = await supabase
      .from('user_follows')
      .select(`
        *,
        following:user_profiles!user_follows_following_id_fkey(*)
      `)
      .eq('follower_id', targetUserId);

    if (error) {
      console.error('Error fetching following:', error);
      return [];
    }

    return (data || []).filter((f): f is typeof f & { following: NonNullable<typeof f.following> } => f.following !== null);
  },

  async getFollowers(userId?: string): Promise<(UserFollow & { follower: UserProfile })[]> {
    const targetUserId = userId || (await supabase.auth.getUser()).data.user?.id;
    if (!targetUserId) return [];

    const { data, error } = await supabase
      .from('user_follows')
      .select(`
        *,
        follower:user_profiles!user_follows_follower_id_fkey(*)
      `)
      .eq('following_id', targetUserId);

    if (error) {
      console.error('Error fetching followers:', error);
      return [];
    }

    return (data || []).filter((f): f is typeof f & { follower: NonNullable<typeof f.follower> } => f.follower !== null);
  },

  async followUser(followingId: string): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || user.id === followingId) return false;

    // Ensure both user profiles exist
    await ensureUserProfile(user.id);
    await ensureUserProfile(followingId);

    const { error } = await supabase
      .from('user_follows')
      .insert({
        follower_id: user.id,
        following_id: followingId
      });

    if (error) {
      console.error('Error following user:', error);
      return false;
    }

    return true;
  },

  async unfollowUser(followingId: string): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { error } = await supabase
      .from('user_follows')
      .delete()
      .eq('follower_id', user.id)
      .eq('following_id', followingId);

    if (error) {
      console.error('Error unfollowing user:', error);
      return false;
    }

    return true;
  },

  async getSuggestedUsers(limit: number = 3): Promise<UserProfile[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data: following, error: followingError } = await supabase
      .from('user_follows')
      .select('following_id')
      .eq('follower_id', user.id);

    if (followingError) {
      console.error('Error fetching following users:', followingError);
      return [];
    }
    
    const followingIds = following.map(f => f.following_id);

    const { data: users, error: usersError } = await supabase
      .from('user_profiles')
      .select(PUBLIC_PROFILE_COLUMNS)
      .neq('id', user.id) // exclude self
      .order('total_xp', { ascending: false }) // suggest high XP users
      .limit(20); // fetch a pool of users

    if (usersError) {
      console.error('Error fetching suggested users:', usersError);
      return [];
    }

    const suggestions = users.filter(u => !followingIds.includes(u.id));

    return suggestions.slice(0, limit) as UserProfile[];
  },

  async getRecentActivities(limit: number = 10): Promise<(UserActivity & { user: UserProfile })[]> {
    const { data, error } = await supabase
      .from('user_activities')
      .select(`
        *,
        user:user_profiles(id, username, full_name, bio, avatar_url, avatar_background, total_xp, current_streak, longest_streak, current_league, league_position, achievements_unlocked, created_at, updated_at, last_active_date)
      `)
      .eq('is_public', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching activities:', error);
      return [];
    }

    return (data || []) as (UserActivity & { user: UserProfile })[];
  }
};

// Helper function to ensure user profile exists
const ensureUserProfile = async (userId: string) => {
  const { data: existingProfile } = await supabase
    .from('user_profiles')
    .select('id')
    .eq('id', userId)
    .maybeSingle();

  if (!existingProfile) {
    await supabase
      .from('user_profiles')
      .insert({
        id: userId,
        username: `user_${userId.substring(0, 8)}`,
        full_name: 'User',
        total_xp: 0
      });
  }
};
